import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Panel, useChartColors } from 'src/UIKit';
import {
  areMetricTotalsEmpty,
  chartCardContentSx,
  chartCardHeaderSx,
  chartCardSx,
  CHART_TICK_FONT_SIZE,
  creditChartLines,
  CREDIT_CHART_TYPES,
  formatMetricValue,
  getMetricTotals,
  getYAxisWidth,
} from '../../../helpers';
import ChartTypeToggle from '../../ChartTypeToggle';
import ChartEmptyState from '../ChartEmptyState';
import ChartLegend from '../ChartLegend';
import { ChartType, MetricChartData } from '../../../types';

const STACK_ID = 'credits';

type TooltipProps = {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
};

function CreditsTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
  return (
    <Panel sx={{ p: 1 }}>
      <Typography component="p" variant="caption" fontWeight="fontWeightSemiBold" mb={0.5}>
        {label}
      </Typography>
      {payload.map(entry => (
        <Stack key={entry.name} direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" color={entry.color}>
            {entry.name}
          </Typography>
          <Typography variant="caption" fontWeight="fontWeightSemiBold">
            {formatMetricValue(entry.value)}
          </Typography>
        </Stack>
      ))}
      <Stack direction="row" justifyContent="space-between" spacing={2} mt={0.5}>
        <Typography variant="caption" fontWeight="fontWeightBold" color="text.primary">
          Total
        </Typography>
        <Typography variant="caption" fontWeight="fontWeightBold" color="text.primary">
          {formatMetricValue(total)}
        </Typography>
      </Stack>
    </Panel>
  );
}

export default function CreditsChartCard({
  data,
  type,
  onTypeChange,
}: {
  data: MetricChartData[];
  type: ChartType;
  onTypeChange: (type: ChartType) => void;
}) {
  const { grid: chartGridStroke } = useChartColors();
  const totals = getMetricTotals(data);
  const isEmpty = areMetricTotalsEmpty(totals, creditChartLines);
  const yAxisWidth = getYAxisWidth(data, creditChartLines, { stacked: true });

  const axes = (
    <>
      <CartesianGrid vertical={false} stroke={chartGridStroke} />
      <XAxis dataKey="label" tick={{ fontSize: CHART_TICK_FONT_SIZE }} minTickGap={16} />
      <YAxis tickFormatter={formatMetricValue} tick={{ fontSize: CHART_TICK_FONT_SIZE }} width={yAxisWidth} />
      <Tooltip content={<CreditsTooltip />} />
    </>
  );

  const renderChart = () => {
    if (type === 'bar') {
      return (
        <BarChart data={data} margin={{ top: 5, right: 25, bottom: 5, left: 0 }}>
          {axes}
          {creditChartLines.map(line => (
            <Bar key={line.dataKey} dataKey={line.dataKey} name={line.name} stackId={STACK_ID} fill={line.color} animationDuration={300} />
          ))}
        </BarChart>
      );
    }
    if (type === 'line') {
      return (
        <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          {axes}
          {creditChartLines.map(line => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              animationDuration={300}
              // recharts v2 LineProps omits stackId though runtime supports it
              {...({ stackId: STACK_ID } as Record<string, unknown>)}
            />
          ))}
        </LineChart>
      );
    }
    return (
      <AreaChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
        <defs>
          {creditChartLines.map(line => (
            <linearGradient key={line.dataKey} id={`${line.dataKey}Stack`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={line.color} stopOpacity={0.5} />
              <stop offset="95%" stopColor={line.color} stopOpacity={0.15} />
            </linearGradient>
          ))}
        </defs>
        {axes}
        {creditChartLines.map(line => (
          <Area
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stackId={STACK_ID}
            stroke={line.color}
            fill={`url(#${line.dataKey}Stack)`}
            fillOpacity={1}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            animationDuration={300}
          />
        ))}
      </AreaChart>
    );
  };

  return (
    <Card variant="outlined" sx={chartCardSx}>
      <CardContent sx={{ ...chartCardContentSx, height: 400 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={chartCardHeaderSx}>
          <Typography fontSize="1.1rem" fontWeight="fontWeightSemiBold">Credits</Typography>
          {!isEmpty && <ChartTypeToggle value={type} onChange={onTypeChange} types={CREDIT_CHART_TYPES} />}
        </Stack>
        <Box flex={1} minHeight={0}>
          {isEmpty ? (
            <ChartEmptyState />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          )}
        </Box>
        {!isEmpty && <ChartLegend lines={creditChartLines} totals={totals} />}
      </CardContent>
    </Card>
  );
}
